/**
 * Organization Service
 * 
 * Handles all organization-related API calls
 */

import { api, handleApiError } from './api';
import type { PaginationParams, PaginatedResponse } from '@/types';

/**
 * Organization Interface
 */
export interface Organization {
  id: number;
  name: string;
  cnpj?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Organization Service Object
 */
export const organizationService = {
  /**
   * Get All Organizations
   * Fetches paginated list of organizations
   * 
   * @param params - Pagination parameters
   * @returns Promise with paginated organization list
   */
  getAll: async (
    params: PaginationParams
  ): Promise<PaginatedResponse<Organization>> => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        size: params.size.toString(),
        ...(params.sort && { sort: params.sort }),
      });

      const response = await api.get<any>(
        `/organizations?${queryParams.toString()}`
      );

      // Log para debug
      if (import.meta.env.DEV) {
        console.log('📊 Organizations API Response:', response.data);
      }

      // Verificar se a resposta tem o formato esperado
      if (!response.data) {
        throw new Error('Invalid response format: missing data');
      }

      const responseData = response.data;
      
      // Se a resposta tem o formato Spring Data (content, pageable, totalPages)
      if (responseData.content && responseData.pageable !== undefined) {
        return {
          data: responseData.content,
          pagination: {
            page: responseData.number || 0,
            size: responseData.size || params.size,
            total: responseData.totalElements || 0,
            totalPages: responseData.totalPages || 1,
            hasNext: !responseData.last,
            hasPrev: !responseData.first,
          },
        };
      }

      // Fallback: se a resposta for um array direto
      if (Array.isArray(responseData)) {
        return {
          data: responseData,
          pagination: {
            page: params.page,
            size: params.size,
            total: responseData.length,
            totalPages: Math.ceil(responseData.length / params.size) || 1,
            hasNext: false,
            hasPrev: false,
          },
        };
      }

      // Se já tiver o formato esperado
      return responseData;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },

  /**
   * Get Organization by ID
   * 
   * @param id - Organization ID
   * @returns Promise with organization details
   */
  getById: async (id: number): Promise<Organization> => {
    try {
      const response = await api.get<Organization>(`/organizations/${id}`);
      return response.data;
    } catch (error) {
      const apiError = handleApiError(error as any);
      throw new Error(apiError.message);
    }
  },
};

export default organizationService;
